import React, { useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import bannerImage from './Images/contactbanner.png';
import { api } from '../lib/api';
import { SITE_URL } from '../lib/seo';

const ENQUIRY_TYPES = ['demo', 'pilot', 'festival', 'general'];

export default function ContactPage() {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type');
  const initialType = ENQUIRY_TYPES.includes(requestedType) ? requestedType : 'general';

  const renderedAt = useRef(Date.now());
  const [form, setForm] = useState({
    type: initialType,
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: '',
    website: '' // honeypot — left empty by real users
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const enquiryLabels = useMemo(
    () => ({
      demo: t('contact.enquiryTypes.demo'),
      pilot: t('contact.enquiryTypes.pilot'),
      festival: t('contact.enquiryTypes.festival'),
      general: t('contact.enquiryTypes.general')
    }),
    [t]
  );

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setFieldErrors({});

    try {
      await api.submitContact({ ...form, formRenderedAt: renderedAt.current });
      setStatus('success');
      setForm((prev) => ({ ...prev, name: '', organization: '', email: '', phone: '', message: '' }));
    } catch (err) {
      if (err.status === 400 && err.data && err.data.errors) {
        setFieldErrors(err.data.errors);
      }
      setStatus('error');
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Contact | Al-Amen Technology</title>
        <meta name="description" content="Request a platform demo, discuss a pilot, book a Dubai AI Festival meeting, or reach the Al-Amen Technology team." />
        <link rel="canonical" href={`${SITE_URL}/contact`} />
      </Helmet>

      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: `linear-gradient(180deg, rgba(11,18,32,0.65), rgba(11,18,32,0.85)), url(${bannerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <Typography variant="h3" fontWeight={900} sx={{ mb: 1 }}>
              {t('contact.heroTitle')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.92, maxWidth: 920 }}>
              {t('contact.heroSubtitle')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper
              component={motion.div}
              variant="outlined"
              sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 5, mb: 3 }}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
                {t('contact.cardTitle')}
              </Typography>
              <Typography sx={{ opacity: 0.9, mb: 2 }}>{t('contact.company')}</Typography>

              <Stack spacing={0.7} sx={{ opacity: 0.9 }}>
                <Typography>{t('contact.address1')}</Typography>
                <Typography>{t('contact.address2')}</Typography>
                <Typography>{t('contact.address3')}</Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ mt: 3 }}>
                <Box>
                  <Typography fontWeight={800}>{t('common.email')}</Typography>
                  <MuiLink href="mailto:ali@al-amentech.io">ali@al-amentech.io</MuiLink>
                </Box>
                <Box>
                  <Typography fontWeight={800}>{t('common.phone')}</Typography>
                  <MuiLink href="tel:+971543282815">+971543282815</MuiLink>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                <Button variant="contained" href="https://maps.app.goo.gl/Kb8odf2hqcSn2M6c7" target="_blank" rel="noopener noreferrer">
                  {t('common.seeOnMap')}
                </Button>
                <Button variant="outlined" href="mailto:ali@al-amentech.io">
                  {t('common.emailUs')}
                </Button>
              </Stack>
            </Paper>

            <Paper
              component={motion.div}
              variant="outlined"
              sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 5, bgcolor: 'rgba(102,126,234,0.08)' }}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: 'easeOut', delay: shouldReduceMotion ? 0 : 0.05 }}
            >
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
                {t('common.whatToExpect')}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>{t('common.whatToExpectBody')}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              variant="outlined"
              sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 5 }}
            >
              <Typography variant="h5" fontWeight={900} sx={{ mb: 0.5 }}>
                {t('contact.formTitle')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 3 }}>{t('contact.formIntro')}</Typography>

              {status === 'success' ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <strong>{t('contact.successTitle')}</strong> {t('contact.successBody')}
                </Alert>
              ) : null}
              {status === 'error' && Object.keys(fieldErrors).length === 0 ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <strong>{t('contact.errorTitle')}</strong> {t('contact.errorBody')}
                </Alert>
              ) : null}

              <Stack spacing={2.5}>
                <FormControl fullWidth>
                  <Typography component="label" htmlFor="enquiry-type" variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                    {t('contact.enquiryTypeLabel')} *
                  </Typography>
                  <Select
                    id="enquiry-type"
                    value={form.type}
                    onChange={handleChange('type')}
                    size="small"
                  >
                    {ENQUIRY_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {enquiryLabels[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={`${t('contact.nameLabel')} *`}
                  value={form.name}
                  onChange={handleChange('name')}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  autoComplete="name"
                  fullWidth
                  required
                />
                <TextField
                  label={`${t('contact.organizationLabel')} *`}
                  value={form.organization}
                  onChange={handleChange('organization')}
                  error={!!fieldErrors.organization}
                  helperText={fieldErrors.organization}
                  autoComplete="organization"
                  fullWidth
                  required
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={`${t('contact.emailLabel')} *`}
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                    autoComplete="email"
                    fullWidth
                    required
                  />
                  <TextField
                    label={t('contact.phoneLabel')}
                    value={form.phone}
                    onChange={handleChange('phone')}
                    error={!!fieldErrors.phone}
                    helperText={fieldErrors.phone}
                    autoComplete="tel"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label={`${t('contact.messageLabel')} *`}
                  value={form.message}
                  onChange={handleChange('message')}
                  error={!!fieldErrors.message}
                  helperText={fieldErrors.message}
                  placeholder={t('contact.messagePlaceholder')}
                  multiline
                  minRows={4}
                  fullWidth
                  required
                />

                {/* Honeypot — visually hidden and unreachable by keyboard/AT, left blank by humans */}
                <Box sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999, top: 'auto' }}>
                  <TextField
                    label="Website"
                    autoComplete="off"
                    value={form.website}
                    onChange={handleChange('website')}
                    inputProps={{ tabIndex: -1, 'aria-hidden': true }}
                    InputLabelProps={{ 'aria-hidden': true }}
                  />
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={status === 'submitting'}
                    startIcon={status === 'submitting' ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {status === 'submitting' ? t('contact.submitting') : t('contact.submit')}
                  </Button>
                  <FormHelperText sx={{ m: 0 }}>{t('contact.requiredNote')}</FormHelperText>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('contact.privacyNote')}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
